import React from 'react';
import { useTranslation } from 'react-i18next';

import { ActionMenu, Icon } from '../../../../components';
import { GlyphBtn, RowPreviewBtn } from './ListActions.styles';

export type ListActionsProps = {
  /** The row is short of room: Preview on site drops its label too, which becomes its tooltip and name. */
  compact: boolean;
  /** There is something to go back from: unpublished changes, a publish to undo, or both. */
  canRevert: boolean;
  /** Put the list back where it started: drop unpublished changes and undo the publish. */
  onRevert: () => void;
  onPreview: () => void;
  /** Configure this list (its games, its shape and what it hides). Only the owner's own lists can be configured; their
   *  name is renamed in place, in the list's heading. */
  onEdit?: () => void;
  /** Hide this list from the page, or show it again (`hidden`): the same as its row's Hide / Show in Reorder lists.
   *  Offered where the page can be arranged and shows the list. */
  onToggleHidden?: () => void;
  /** The page hides this list. */
  hidden?: boolean;
  /** Delete this list (one of the owner's own), offered in the kebab; the caller asks before it deletes. */
  onDelete?: () => void;
  /** One of the owner's own lists is being stored (a rename, a reorder): Configure list waits for it, so the editor
   *  opens on what was stored, not on what the write is about to replace. */
  listWriting?: boolean;
  /** The write in flight, if any: every write waits for it. */
  writing: 'publish' | 'revert' | null;
};

/**
 * The actions that belong to ONE game list, in the order they read on its row: Hide list / Show list (where the page can
 * be arranged) and Configure list (its own lists only), their glyphs alone with their names as tooltips; Preview on
 * site; and, behind the kebab as the carousel's are, Revert to initial state (which drops unpublished changes and undoes
 * the publish) and Delete this list (its own lists only). A list is published with the rest of the page by the tab's
 * Publish changes, the one publish there is. Shared by the Game Lists tab and the reorder sheet, so both rows are
 * the same.
 */
export const ListActions = ({
  compact,
  canRevert,
  onRevert,
  onPreview,
  onEdit,
  onToggleHidden,
  hidden = false,
  onDelete,
  writing,
  listWriting = false,
}: ListActionsProps): React.ReactElement => {
  const { t } = useTranslation();
  const iconOnly = (label: string): { 'aria-label': string; title: string } => ({ 'aria-label': label, title: label });

  return (
    <>
      {onToggleHidden ? (
        // Literal strings per branch, so the extractor harvests both.
        // Held while a write runs, as Reorder lists' Save order is.
        // Its glyph alone, its name its tooltip (the user, 2026-10-20), as Configure list's. The eye is the list's state
        // as the row's is (the user, 2026-10-21): open while it shows, crossed out and held on while hidden.
        <GlyphBtn
          type="button"
          $on={hidden}
          disabled={writing !== null}
          onClick={onToggleHidden}
          {...iconOnly(hidden ? t('Show list') : t('Hide list'))}
        >
          <Icon name={hidden ? 'eye-off' : 'eye'} size={14} />
        </GlyphBtn>
      ) : null}
      {onEdit ? (
        <GlyphBtn
          type="button"
          disabled={writing !== null || listWriting}
          onClick={onEdit}
          {...iconOnly(t('Configure list'))}
        >
          <Icon name="settings" size={14} />
        </GlyphBtn>
      ) : null}
      {/* In Add list's look (the shared outline Button), its own glyph rather than Show list's eye. */}
      {compact ? (
        <GlyphBtn type="button" onClick={onPreview} {...iconOnly(t('Preview on site'))}>
          <Icon name="preview" size={14} />
        </GlyphBtn>
      ) : (
        <RowPreviewBtn variant="outline" icon="preview" label="Preview on site" onClick={onPreview} />
      )}
      {canRevert || onDelete ? (
        <ActionMenu
          align="end"
          label="More actions"
          disabled={writing !== null}
          items={[
            ...(canRevert
              ? [
                  {
                    label: writing === 'revert' ? 'Reverting…' : 'Revert to initial state',
                    icon: 'arrow-left' as const,
                    disabled: writing !== null,
                    onSelect: onRevert,
                  },
                ]
              : []),
            ...(onDelete
              ? [
                  {
                    label: 'Delete this list',
                    icon: 'trash' as const,
                    destructive: true,
                    disabled: writing !== null,
                    onSelect: onDelete,
                  },
                ]
              : []),
          ]}
        />
      ) : null}
    </>
  );
};

export default ListActions;
